import './PermissionPage.css';

import { ILockService, lockErrorKind, lockErrorMessage, lockService } from '../../../locks/services/LockService';
import { IdentityUnavailableState } from '../../../locks/components/LockStates';
import { Link, useParams } from 'react-router-dom';
import { identityWebIamUrl, spaceScope } from '../../../locks/utils/identityWeb';
import { useCallback, useEffect, useState } from 'react';

import { DateTimeHelper } from '../../../../lib/utils/DateTimeHelper';
import { Loader } from '../../../../lib/components/loader/Loader';
import { PageContainer } from '../../../../lib/layouts/containers/page/PageContainer';
import { PageLockDTO } from '../../../locks/models/LockDTO';

interface IPermissionPageProps {
    /** Injected in tests; the shared facade client otherwise. */
    service?: ILockService;
}

type LocksState =
    | { status: 'loading' }
    | { status: 'ready'; locks: PageLockDTO[] }
    | { status: 'identity-unavailable' }
    | { status: 'error'; message: string };

/**
 * Space permissions (OrbitalPlan/18 D8). Who may read and edit a space is an Identity Core assignment at the space's scope, managed in
 * Identity Web; Ark only records where locks are. This page explains that, links to the space's IAM page and lists the space's locks.
 */
export function PermissionPage(props: IPermissionPageProps) {
    const { spaceId } = useParams();
    const service = props.service ?? lockService();
    const [locks, setLocks] = useState<LocksState>({ status: 'loading' });

    const load = useCallback(() => {
        if (!spaceId) return;
        setLocks({ status: 'loading' });
        service
            .listLocks(spaceId)
            .then((list) => setLocks({ status: 'ready', locks: list }))
            .catch((err) =>
                setLocks(lockErrorKind(err) === 'identity-unavailable'
                    ? { status: 'identity-unavailable' }
                    : lockErrorKind(err) === 'not-found'
                        // a server without lock routes (pre-1b): nothing is locked there
                        ? { status: 'ready', locks: [] }
                        : { status: 'error', message: lockErrorMessage(err) }));
    }, [service, spaceId]);

    useEffect(() => {
        load();
    }, [load]);

    const iamUrl = spaceId ? identityWebIamUrl(spaceScope(spaceId)) : undefined;

    return (
        <PageContainer className="edit-permission-page">
            <h1>Permissions</h1>

            <section className="permissions-section" aria-label="Space access">
                <p>
                    Who can read and edit this space is managed in Identity, not in Ark: people and groups get an Ark role
                    (Ark Reader, Ark Contributor or Ark Owner) on the space, on Ark or on the whole platform. Contributors and Owners
                    can lock pages; space Owners and platform admins read every locked page.
                </p>
                {iamUrl ? (
                    <a className="button primary permissions-manage" href={iamUrl} target="_blank" rel="noopener noreferrer">
                        Manage access in Identity Web
                    </a>
                ) : (
                    <p className="permissions-muted">Identity Web is not configured for this environment (App.IdentityWebUrl).</p>
                )}
            </section>

            <section className="permissions-section" aria-label="Locked pages">
                <h2>Locked pages</h2>
                <p className="permissions-muted">
                    A lock hides a page and its children from everyone except the lock's readers, space Owners and platform admins.
                    Lock and unlock a page from the page itself. Only the locks you can read are listed.
                </p>
                {locks.status === 'loading' ? <Loader /> : null}
                {locks.status === 'identity-unavailable' ? <IdentityUnavailableState compact onRetry={load} /> : null}
                {locks.status === 'error' ? <div className="permissions-error">{locks.message}</div> : null}
                {locks.status === 'ready' && locks.locks.length === 0 ? (
                    <p>No locked pages that you can read in this space.</p>
                ) : null}
                {locks.status === 'ready' && locks.locks.length > 0 ? (
                    <table className="permissions-locks">
                        <thead>
                            <tr>
                                <th>Page</th>
                                <th>Reason</th>
                                <th>Locked on</th>
                                <th>Readers</th>
                            </tr>
                        </thead>
                        <tbody>
                            {locks.locks.map((lock) => {
                                const manageUrl = lock.manageUrl || identityWebIamUrl(lock.scope);
                                return (
                                    <tr key={lock.pageId}>
                                        <td>
                                            <span className="lock-badge" aria-hidden="true">{'\u{1F512}'}</span>{' '}
                                            <Link to={`/spaces/${spaceId}/pages/${lock.pageId}`}>{lock.title || `#${lock.pageId}`}</Link>
                                        </td>
                                        <td>{lock.reason || ''}</td>
                                        <td>{DateTimeHelper.formatDateTime(lock.createdAt)}</td>
                                        <td>
                                            {manageUrl ? (
                                                <a href={manageUrl} target="_blank" rel="noopener noreferrer"
                                                    aria-label={`Manage readers of ${lock.title || `#${lock.pageId}`}`}>
                                                    Manage readers
                                                </a>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : null}
            </section>
        </PageContainer>
    );
}
